'use client';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Navbar from '@/components/Navbar';
import AboutSection from '@/components/AboutSection';
import Footer from '@/components/Footer';
import AirportSearchInput from '@/components/AirportSearchInput';
import Link from 'next/link';
import BlogArticlesPreview from '@/components/BlogArticlesPreview';
import FaqAccordion from '@/components/FaqAccordion';
import { Plane, MapPin, Star, Shield, Clock, ArrowRight, Globe, Phone, X, CheckCircle, Building2, ChevronRight, AlertCircle, ChevronDown } from 'lucide-react';

const HOME_INTRO_PARAGRAPHS = [
  'En VuelosExpertos, hacemos que la planificación de viajes sea más fácil al ofrecer asistencia confiable para reservas de vuelos, paquetes vacacionales y soluciones de viaje completas. Nuestro equipo te ayuda a encontrar las mejores opciones de vuelo con las principales aerolíneas, brindando soporte para reservas, selección de asientos, servicios de equipaje, cambios de vuelo, cancelaciones y solicitudes de reembolso.',
  'Ya sea que estés planeando unas vacaciones familiares, un viaje de negocios o una escapada relajante, VuelosExpertos ofrece opciones de viaje adaptadas a tus necesidades. Disponemos de paquetes vacacionales que combinan vuelos, hoteles y experiencias de viaje, ayudándote a ahorrar tiempo y disfrutar de una experiencia más organizada.',
  'Además de reservar vuelos, también puedes gestionar hoteles cómodos y servicios de alquiler de coches a través de VuelosExpertos. Nuestro objetivo es facilitar cada parte de tu viaje, desde la salida hasta la llegada. Con opciones flexibles y atención personalizada, ayudamos a los viajeros a crear experiencias inolvidables sin complicaciones.',
  'Nuestro equipo de atención al cliente está disponible las 24 horas del día, los 7 días de la semana, para brindarte ayuda cuando la necesites. Ya sea para reservar un nuevo vuelo, modificar una reserva existente o encontrar el paquete vacacional ideal, VuelosExpertos está listo para ayudarte.',
  'Elige VuelosExpertos para recibir asistencia de viaje confiable, soluciones completas de reserva y una experiencia personalizada adaptada a tus preferencias.',
];

const FEATURES = [
  {
    icon: Shield,
    title: 'Los precios asequibles',
    desc: 'Aquí puede reservar los billetes a precios mínimos'
  },
  {
    icon: Clock,
    title: 'Ayuda 24/7',
    desc: 'Los agentes están disponibles 24/7 para brindarle apoyo lo antes posible'
  },
  {
    icon: Globe,
    title: 'Reserva fácil y rápida',
    desc: 'Reservar pasajes sin complicaciones y más rápidamente'
  },
  {
    icon: Star,
    title: 'El viaje seguro y confiable',
    desc: 'Reserva tu vuelo con Vuelosexpertos con total confianza: somos un sitio seguro y elegido por miles de pasajeros'
  },
];

export default function HomeClient() {
  const router = useRouter();
  const [tripType, setTripType] = useState('Ida y Vuelta');
  const [homeLocations, setHomeLocations] = useState([]);
  const [latestBlog, setLatestBlog] = useState(null);
  const [latestArticle, setLatestArticle] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [introExpanded, setIntroExpanded] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState({
    from: '',
    to: '',
    departure: '',
    returnDate: '',
    passengers: '1 Adult',
    cabinClass: 'Economy',
  });

  function handleField(k, v) { setForm(f => ({ ...f, [k]: v })); if (validationError) setValidationError(''); }

  useEffect(() => {
    fetch('/api/locations').then(r => r.json()).then(d => setHomeLocations(d.slice(0, 6))).catch(() => {});
    fetch('/api/latest-posts').then(r => r.json()).then(d => { setLatestBlog(d.latestBlog); setLatestArticle(d.latestArticle); }).catch(() => {});
    fetch('/api/faqs').then(r => r.json()).then(d => setFaqs(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  function handleSearch() {
    if (!form.from.trim()) { setValidationError('Por favor, introduce tu ciudad o aeropuerto de salida.'); return; }
    if (!form.to.trim()) { setValidationError('Por favor, introduce tu ciudad o aeropuerto de destino.'); return; }
    if (!form.departure) { setValidationError('Por favor, selecciona una fecha de salida.'); return; }
    if (tripType === 'Ida y Vuelta' && !form.returnDate) { setValidationError('Por favor, selecciona una fecha de regreso para tu viaje de ida y vuelta.'); return; }
    setValidationError('');
    const q = new URLSearchParams({
      from: form.from,
      to: form.to,
      departure: form.departure,
      return: form.returnDate,
      passengers: form.passengers,
      class: form.cabinClass,
      trip: tripType,
    });
    router.push(`/vuelos?${q.toString()}`);
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative flex items-center justify-center bg-slate-900">
        <img
          src="/images/hero-flight-sky.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-linear-to-b from-slate-900/60 via-slate-900/30 to-slate-900" />
        <div className="absolute top-32 left-12 opacity-20 animate-bounce" style={{ animationDuration: '3s' }}>
          <Plane size={28} className="text-white -rotate-12" />
        </div>
        <div className="absolute top-48 right-20 opacity-15 animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
          <Plane size={18} className="text-sky-400 rotate-12" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto py-10 lg:py-16">
          <div className="inline-flex items-center gap-2 bg-sky-500/20 border border-sky-500/30 text-sky-300 text-sm px-4 py-2 rounded-full mb-6 backdrop-blur-sm">
            <span className="w-2 h-2 bg-sky-400 rounded-full animate-pulse" />
            Vuelosexpertos sigue escalando
          </div>
          <h1 className="font-display text-5xl md:text-5xl font-bold text-white mb-6 leading-none">
            Planea, reserva y <span className="text-sky-400">vuela con confianza</span>
          </h1>
          <p className="text-slate-300 text-lg md:text-xl mx-auto mb-12 leading-relaxed lg:px-24">
            No importa si está planificando un viaje con sus amigos o su familia; los ejecutivos de nuestra compañía siempre están aquí para ayudarle. Además, con el apoyo de <Link href="/" className="text-sky-300 font-semibold hover:underline">vuelosexpertos.com</Link>, puede reservar el billete de vuelo a precios mínimos.
          </p>

          {/* Search Box */}
          <div id="search" className="bg-white rounded-2xl p-4 md:p-6 shadow-2xl max-w-4xl mx-auto">
            <div className="flex gap-4 mb-5 border-b border-slate-100 pb-4">
              {['Solo Ida', 'Ida y Vuelta'].map((t) => (
                <button key={t} onClick={() => setTripType(t)} className={`text-sm font-medium px-3 py-1 rounded-full transition-colors ${tripType === t ? 'bg-sky-500 text-white' : 'text-slate-500 hover:text-slate-800'}`}>
                  {t}
                </button>
              ))}
            </div>

            {/* Validation error popup */}
            {validationError && (
              <div className="mb-4 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                <AlertCircle size={18} className="flex-shrink-0 text-red-500" />
                <span className="flex-1">{validationError}</span>
                <button onClick={() => setValidationError('')} aria-label="Descartar error" className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0">
                  <X size={16} />
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <AirportSearchInput
                label="Desde"
                value={form.from}
                onChange={v => handleField('from', v)}
                placeholder="Atlanta (ATL)"
              />
              <AirportSearchInput
                label="Hacia"
                value={form.to}
                onChange={v => handleField('to', v)}
                placeholder="¿A dónde vas?"
              />
              <div>
                <label htmlFor="search-departure" className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">Salida</label>
                <input id="search-departure" type="date" className="w-full px-3 py-3 rounded-xl border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm" value={form.departure} onChange={e => handleField('departure', e.target.value)} />
              </div>
              <div>
                <label htmlFor="search-return" className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">Regreso</label>
                <input id="search-return" type="date" className={`w-full px-3 py-3 rounded-xl border text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm transition-colors ${tripType === 'Solo Ida' ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed' : 'border-slate-200'}`}
                  value={form.returnDate} onChange={e => handleField('returnDate', e.target.value)} disabled={tripType === 'Solo Ida'} />
              </div>
            </div>
            <div className="flex gap-3 mt-4 items-end">
              <div className="flex-1">
                <label htmlFor="search-passengers" className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">Pasajeros</label>
                <select id="search-passengers" className="w-full px-3 py-3 rounded-xl border border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm" value={form.passengers} onChange={e => handleField('passengers', e.target.value)}>
                  <option>1 Adult</option><option>2 Adults</option><option>2 Adults, 1 Child</option><option>2 Adults, 2 Children</option>
                </select>
              </div>
              <div className="flex-1">
                <label htmlFor="search-cabin-class" className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">Clase</label>
                <select id="search-cabin-class" className="w-full px-3 py-3 rounded-xl border border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm" value={form.cabinClass} onChange={e => handleField('cabinClass', e.target.value)}>
                  <option>Economy</option><option>Premium Economy</option><option>Business</option><option>First Class</option>
                </select>
              </div>
              <button onClick={handleSearch} className="bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-3 rounded-xl transition-colors flex items-center gap-2 whitespace-nowrap">
                <Plane size={18} /> Buscar Vuelos
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-sky-500 text-white py-6">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[['500+', 'Aerolíneas'], ['120+', 'Países'], ['100K+', 'Viajeros satisfechos'], ['$50', 'Ahorro promedio']].map(([n, l]) => (
            <div key={l}><div className="text-3xl font-display font-bold">{n}</div><div className="text-sky-100 text-sm mt-1">{l}</div></div>
          ))}
        </div>
      </section>

      {/* Intro content */}
      <section className="py-14 px-6 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            ¿Qué Ofrece VuelosExpertos Para Tu Viaje?
          </h2>
          <div className="text-slate-600 text-base md:text-lg leading-relaxed space-y-4">
            <p>{HOME_INTRO_PARAGRAPHS[0]}</p>
            {introExpanded && HOME_INTRO_PARAGRAPHS.slice(1).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          {HOME_INTRO_PARAGRAPHS.length > 1 && (
            <button
              type="button"
              onClick={() => setIntroExpanded(v => !v)}
              aria-expanded={introExpanded}
              className="mt-3 inline-flex items-center gap-1.5 text-sky-600 hover:text-sky-700 font-semibold text-sm transition-colors"
            >
              {introExpanded ? 'Leer menos' : 'Leer más'}
              <ChevronDown size={16} className={`transition-transform ${introExpanded ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </section>

      {/* About Section */}
      <AboutSection />

      {/* Features */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-500 font-semibold text-sm tracking-widest uppercase mb-2">
              ¿Por qué Vuelosexpertos?
            </p>

            <h2 className="font-display text-4xl font-bold text-slate-900">
              Viaja con confianza
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="text-center px-6 py-7 shadow-md rounded-[20px] border border-gray-100">
                <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon size={26} className="text-sky-500" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2 text-[18px]">{title}</h3>
                <p className="text-slate-500 text-md leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog & Articles Preview */}
      <BlogArticlesPreview latestBlog={latestBlog} latestArticle={latestArticle} />

      {/* Global FAQs — shown just above the footer */}
      <div className='faqs-home'>
          <FaqAccordion faqs={faqs} />
      </div>

      {/* LARGE FOOTER */}
      <Footer />
    </div>
  );
}
